import type { AIStop } from "@/lib/ai-stops.functions";
import type { ProPlan } from "@/lib/pro/pipeline.functions";

/**
 * Dados locais do modo de teste. Nunca chamam IA, Places ou Directions.
 * Servem só para validar navegação, ecrãs Premium/Pro e exportações no
 * telemóvel durante o teste interno.
 */

const DEMO_CITY = "Lisboa";

export const DEMO_CENTER = { lat: 38.7223, lng: -9.1393 };

const BASE: Array<Omit<AIStop, "day">> = [
  {
    name: "Praça do Comércio",
    category: "landmark",
    blurb: "Praça ribeirinha e porta de entrada histórica da cidade.",
    priceLocal: 0,
    rating: 4.7,
    lat: 38.7075,
    lng: -9.1364,
  },
  {
    name: "Sé de Lisboa",
    category: "church",
    blurb: "A catedral mais antiga da cidade, românica e sóbria.",
    priceLocal: 5,
    rating: 4.6,
    lat: 38.7098,
    lng: -9.1329,
  },
  {
    name: "Miradouro da Senhora do Monte",
    category: "viewpoint",
    blurb: "A vista mais alta sobre o casario e o rio.",
    priceLocal: 0,
    rating: 4.8,
    lat: 38.7182,
    lng: -9.1319,
  },
  {
    name: "Mosteiro dos Jerónimos",
    category: "monument",
    blurb: "Manuelino em estado puro, em Belém.",
    priceLocal: 12,
    rating: 4.8,
    lat: 38.6979,
    lng: -9.2065,
  },
  {
    name: "Torre de Belém",
    category: "monument",
    blurb: "Torre defensiva do século XVI à beira-Tejo.",
    priceLocal: 8,
    rating: 4.6,
    lat: 38.6916,
    lng: -9.216,
  },
  {
    name: "Time Out Market",
    category: "food",
    blurb: "Mercado gastronómico com cozinha portuguesa contemporânea.",
    priceLocal: 20,
    rating: 4.4,
    lat: 38.7071,
    lng: -9.1459,
  },
];

/** Roteiro Premium de demonstração, distribuído pelos dias pedidos. */
export function demoStops(days = 1): AIStop[] {
  const total = Math.max(1, Math.min(5, days));
  return BASE.map((stop, index) => ({ ...stop, day: (index % total) + 1 }));
}

/** Plano Pro de demonstração com o número de dias pedido. */
export function demoProPlan(input?: { days?: number; title?: string; city?: string }): ProPlan {
  const days = Math.max(1, Math.min(7, input?.days ?? 2));
  const city = input?.city || DEMO_CITY;
  const perDay = Math.max(2, Math.ceil(BASE.length / days));

  return {
    id: undefined,
    title: input?.title || `[Teste] ${city} em ${days} dia${days === 1 ? "" : "s"}`,
    status: "ready",
    transport: "walk",
    pace: "normal",
    mood: "clássico",
    days: Array.from({ length: days }, (_, i) => {
      const slice = BASE.slice(i * perDay, i * perDay + perDay);
      const stops = (slice.length ? slice : BASE.slice(0, 2)).map((stop, j) => ({
        kind: "poi" as const,
        name: stop.name,
        time: `${String(9 + j * 2).padStart(2, "0")}:00`,
        durationMin: 60,
        lat: stop.lat,
        lng: stop.lng,
        note: stop.blurb,
      }));
      return {
        day: i + 1,
        city,
        intro: "Dia de demonstração (modo de teste, sem custos).",
        stops: [
          ...stops,
          {
            kind: "meal" as const,
            name: "Pausa para almoço",
            time: "13:00",
            durationMin: 90,
            lat: DEMO_CENTER.lat,
            lng: DEMO_CENTER.lng,
          },
        ],
      };
    }),
    compliance: {
      valid: true,
      repaired: false,
      violations: [],
      repairCandidates: [],
    },
  } as ProPlan;
}
